import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { MouseEvent as ReactMouseEvent } from "react"

const MARQUEE_THRESHOLD_PX = 4

const INTERACTIVE_SELECTOR = "button, a, input, textarea, [role='button']"
const CONTENT_ITEM_SELECTOR = "[data-kb-content-item='true']"

export interface MarqueeRect {
  left: number
  top: number
  width: number
  height: number
}

interface DragState {
  startX: number
  startY: number
  additive: boolean
  baseSelected: Set<string>
  dragging: boolean
  lastComputedIds: Set<string>
}

export interface UseContentSelectionParams {
  scopeId: string
  itemOrderIds: string[]
}

export interface UseContentSelectionReturn {
  selectedIds: Set<string>
  marqueeDraftIds: Set<string> | null
  primaryId: string | null
  anchorId: string | null
  marqueeRect: MarqueeRect | null
  registerItemElement: (id: string, el: HTMLElement | null) => void
  handleItemClick: (id: string, e: ReactMouseEvent<HTMLElement>) => void
  handleContainerMouseDown: (e: ReactMouseEvent<HTMLElement>) => void
  consumeSuppressClick: () => boolean
  clearSelection: () => void
  selectOnly: (id: string | null) => void
}

const getIdsInRange = (
  itemOrderIds: string[],
  fromId: string,
  toId: string
) => {
  const fromIndex = itemOrderIds.indexOf(fromId)
  const toIndex = itemOrderIds.indexOf(toId)
  if (fromIndex < 0 || toIndex < 0) return new Set<string>([toId])

  const [start, end] =
    fromIndex <= toIndex ? [fromIndex, toIndex] : [toIndex, fromIndex]
  return new Set(itemOrderIds.slice(start, end + 1))
}

const areSetsEqual = (a: Set<string>, b: Set<string>) => {
  if (a.size !== b.size) return false
  for (const value of a) {
    if (!b.has(value)) return false
  }
  return true
}

const intersects = (a: MarqueeRect, b: DOMRect) => {
  return !(
    a.left + a.width < b.left ||
    a.left > b.right ||
    a.top + a.height < b.top ||
    a.top > b.bottom
  )
}

export const useContentSelection = ({
  scopeId,
  itemOrderIds,
}: UseContentSelectionParams): UseContentSelectionReturn => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [marqueeDraftIds, setMarqueeDraftIds] = useState<Set<string> | null>(
    null
  )
  const [primaryId, setPrimaryId] = useState<string | null>(null)
  const [anchorId, setAnchorId] = useState<string | null>(null)
  const [marqueeRect, setMarqueeRect] = useState<MarqueeRect | null>(null)

  const itemElementsRef = useRef<Map<string, HTMLElement>>(new Map())
  const dragStateRef = useRef<DragState | null>(null)
  const suppressClickRef = useRef(false)
  const selectedIdsRef = useRef(selectedIds)
  const primaryIdRef = useRef(primaryId)
  const marqueeDraftIdsRef = useRef<Set<string> | null>(marqueeDraftIds)

  useEffect(() => {
    selectedIdsRef.current = selectedIds
  }, [selectedIds])

  useEffect(() => {
    primaryIdRef.current = primaryId
  }, [primaryId])

  useEffect(() => {
    marqueeDraftIdsRef.current = marqueeDraftIds
  }, [marqueeDraftIds])

  useEffect(() => {
    setSelectedIds(new Set())
    setMarqueeDraftIds(null)
    setPrimaryId(null)
    setAnchorId(null)
    setMarqueeRect(null)
    itemElementsRef.current.clear()
  }, [scopeId])

  useEffect(() => {
    const validIds = new Set(itemOrderIds)
    setSelectedIds((prev) => {
      const next = new Set(Array.from(prev).filter((id) => validIds.has(id)))
      if (areSetsEqual(next, prev)) return prev
      return next
    })
    setMarqueeDraftIds((prev) => {
      if (!prev) return prev
      const next = new Set(Array.from(prev).filter((id) => validIds.has(id)))
      if (areSetsEqual(next, prev)) return prev
      return next.size ? next : null
    })
    setPrimaryId((prev) => (prev && validIds.has(prev) ? prev : null))
    setAnchorId((prev) => (prev && validIds.has(prev) ? prev : null))
  }, [itemOrderIds])

  const commitSingleSelection = useCallback((id: string | null) => {
    if (!id) {
      setSelectedIds(new Set())
      setPrimaryId(null)
      setAnchorId(null)
      return
    }
    setSelectedIds(new Set([id]))
    setPrimaryId(id)
    setAnchorId(id)
  }, [])

  const handleItemClick = useCallback(
    (id: string, e: ReactMouseEvent<HTMLElement>) => {
      const isToggle = e.metaKey || e.ctrlKey
      const isRange = e.shiftKey

      if (isRange) {
        const rangeAnchor = anchorId || id
        const rangeIds = getIdsInRange(itemOrderIds, rangeAnchor, id)

        setSelectedIds((prev) => {
          if (!isToggle) return rangeIds
          const next = new Set(prev)
          for (const rangeId of rangeIds) next.add(rangeId)
          return next
        })
        setPrimaryId(id)
        if (!anchorId) setAnchorId(id)
        return
      }

      if (isToggle) {
        setSelectedIds((prev) => {
          const next = new Set(prev)
          if (next.has(id)) {
            next.delete(id)
            if (next.size === 0) {
              setPrimaryId(null)
              return next
            }
            if (primaryIdRef.current === id) {
              const fallback = itemOrderIds.find((itemId) => next.has(itemId))
              setPrimaryId(fallback || null)
            }
            return next
          }

          next.add(id)
          setPrimaryId(id)
          return next
        })
        setAnchorId(id)
        return
      }

      commitSingleSelection(id)
    },
    [anchorId, commitSingleSelection, itemOrderIds]
  )

  const collectIdsInRect = useCallback((rect: MarqueeRect) => {
    const next = new Set<string>()
    for (const [id, el] of itemElementsRef.current.entries()) {
      if (intersects(rect, el.getBoundingClientRect())) {
        next.add(id)
      }
    }
    return next
  }, [])

  const handleContainerMouseDown = useCallback(
    (e: ReactMouseEvent<HTMLElement>) => {
      if (e.button !== 0) return
      const target = e.target
      if (!(target instanceof Element)) return
      if (target.closest(CONTENT_ITEM_SELECTOR)) return
      if (target.closest(INTERACTIVE_SELECTOR)) return

      const dragState: DragState = {
        startX: e.clientX,
        startY: e.clientY,
        additive: e.metaKey || e.ctrlKey,
        baseSelected: new Set(selectedIdsRef.current),
        dragging: false,
        lastComputedIds: new Set(),
      }
      dragStateRef.current = dragState

      const onMouseMove = (moveEvent: MouseEvent) => {
        const active = dragStateRef.current
        if (!active) return

        const dx = moveEvent.clientX - active.startX
        const dy = moveEvent.clientY - active.startY

        if (!active.dragging) {
          if (
            Math.abs(dx) < MARQUEE_THRESHOLD_PX &&
            Math.abs(dy) < MARQUEE_THRESHOLD_PX
          ) {
            return
          }
          active.dragging = true
          suppressClickRef.current = true
        }

        const rect: MarqueeRect = {
          left: Math.min(active.startX, moveEvent.clientX),
          top: Math.min(active.startY, moveEvent.clientY),
          width: Math.abs(dx),
          height: Math.abs(dy),
        }

        setMarqueeRect(rect)

        const hitIds = collectIdsInRect(rect)
        if (active.additive) {
          const merged = new Set(active.baseSelected)
          for (const id of hitIds) merged.add(id)
          active.lastComputedIds = merged
          setMarqueeDraftIds(merged)
          return
        }
        active.lastComputedIds = hitIds
        setMarqueeDraftIds(hitIds)
      }

      const onMouseUp = () => {
        const active = dragStateRef.current
        dragStateRef.current = null
        window.removeEventListener("mousemove", onMouseMove)
        window.removeEventListener("mouseup", onMouseUp)

        if (!active?.dragging) {
          setMarqueeRect(null)
          setMarqueeDraftIds(null)
          return
        }

        const finalIds =
          active.lastComputedIds.size > 0
            ? active.lastComputedIds
            : marqueeDraftIdsRef.current || new Set<string>()
        setSelectedIds(finalIds)
        setPrimaryId((prevPrimary) => {
          if (prevPrimary && finalIds.has(prevPrimary)) return prevPrimary
          const fallback = itemOrderIds.find((id) => finalIds.has(id))
          return fallback || null
        })
        setMarqueeRect(null)
        setMarqueeDraftIds(null)
      }

      window.addEventListener("mousemove", onMouseMove)
      window.addEventListener("mouseup", onMouseUp)
      e.preventDefault()
    },
    [collectIdsInRect, itemOrderIds]
  )

  const registerItemElement = useCallback(
    (id: string, el: HTMLElement | null) => {
      if (!el) {
        itemElementsRef.current.delete(id)
        return
      }
      itemElementsRef.current.set(id, el)
    },
    []
  )

  const consumeSuppressClick = useCallback(() => {
    if (!suppressClickRef.current) return false
    suppressClickRef.current = false
    return true
  }, [])

  const effectiveSelectedIds = useMemo(
    () => marqueeDraftIds || selectedIds,
    [marqueeDraftIds, selectedIds]
  )

  return {
    selectedIds: effectiveSelectedIds,
    marqueeDraftIds,
    primaryId,
    anchorId,
    marqueeRect,
    registerItemElement,
    handleItemClick,
    handleContainerMouseDown,
    consumeSuppressClick,
    clearSelection: () => commitSingleSelection(null),
    selectOnly: commitSingleSelection,
  }
}

export default useContentSelection
